import { useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Wallet, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { MascotPortrait } from "@/components/Mascot";
import { ShareButtons } from "@/components/ShareWolf";
import { detectWallets, connectWallet, signText, signTx, openInPhantom } from "@/lib/solana";

const STEPS = ["Connect wallet", "Sign", "Review cost", "Pay & mint", "Done"];

function Steps({ step }) {
  return (
    <ol className="flex gap-2 text-[11px] font-mono-game mb-5" data-testid="claim-steps">
      {STEPS.map((s, i) => (
        <li key={s} className={`flex-1 rounded-full px-2 py-1 text-center border ${i < step ? "border-emerald-400/60 text-emerald-300" : i === step ? "border-amber-300 text-amber-200" : "border-slate-700 text-slate-500"}`}>{s}</li>
      ))}
    </ol>
  );
}

export const SolMark = ({ className = "w-3.5 h-3.5" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true"><path fill="currentColor" d="M5 16.5h13.5L16 19H2.5zM5 5h13.5L16 7.5H2.5zm13.5 5.75H5L2.5 13.25H16z" /></svg>
);

function Cost({ cost, enough }) {
  return (
    <div className="rounded-xl border border-amber-400/40 bg-amber-400/5 p-4 text-sm space-y-1" data-testid="claim-cost">
      <p className="text-white">Claiming costs about <b className="text-amber-200" data-testid="claim-cost-total">{cost.total.toFixed(4)} SOL</b></p>
      <p className="text-xs text-slate-400">{cost.mint_fee > 0 ? `${cost.mint_fee} SOL mint fee to Wolfpack Games + ` : "No mint fee, just "}~{cost.network.toFixed(4)} SOL Solana network cost</p>
      <p className="text-xs text-slate-500" data-testid="claim-cost-balance">Your wallet has {cost.balance.toFixed(4)} SOL</p>
      <p className="text-[11px] text-slate-400 pt-2 border-t border-slate-700/60 mt-2" data-testid="claim-wallet-warning-note">
        If Phantom says this site "could be malicious", that's its usual warning for new sites. This payment only sends {cost.mint_fee} SOL to our minting wallet and mints your wolf to you. No token approvals. <a href="/.well-known/wolfpack-nft.json" target="_blank" rel="noreferrer" className="text-cyan-300 hover:underline" data-testid="claim-verify-link">Check our addresses</a>
      </p>
      {!enough && <p className="text-xs text-red-300 pt-1" data-testid="claim-insufficient">You need about {cost.needed} SOL in this wallet to claim. Add SOL and try again.</p>}
    </div>
  );
}

const cancelled = (e) => /reject|cancel|denied|declined/i.test(e?.message || "");

export default function ClaimNftDialog({ look, open, onClose, onClaimed }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState(null);
  const [again, setAgain] = useState(false);
  const wallets = detectWallets();
  const fail = (e) => {
    toast.error(e?.response ? errMsg(e) : cancelled(e) ? "You cancelled. Nothing was charged." : e.message || "Something went wrong. Nothing was charged.");
    setStep(0); setQuote(null); setAgain(false);
  };
  const go = async (id) => {
    setBusy(true);
    try {
      const { provider, address } = await connectWallet(id);
      setStep(1);
      const { data: ch } = await api.post("/sol/challenge", { wallet: address });
      const signature = await signText(provider, ch.message);
      const { data } = await api.post(`/looks/unique/${look.id}/claim`, { wallet: address, nonce: ch.nonce, signature, origin_url: window.location.origin });
      if (data.status === "complete") { setStep(4); onClaimed(); return; }
      setQuote({ ...data, provider });
      setStep(2);
    } catch (e) { fail(e); } finally { setBusy(false); }
  };
  const pay = async () => {
    setBusy(true);
    try {
      const transaction = await signTx(quote.provider, quote.transaction);
      const { data: sub } = await api.post(`/looks/unique/${look.id}/claim/submit`, { transaction });
      if (sub.status === "approve_again") {
        setAgain(true);
        const second = await signTx(quote.provider, sub.transaction);
        await api.post(`/looks/unique/${look.id}/claim/submit`, { transaction: second });
      }
      setAgain(false);
      setStep(3);
      for (let i = 0; i < 50; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data } = await api.get("/looks/unique/mine");
        const n = data.looks.find((l) => l.id === look.id)?.nft;
        if (n?.status === "complete") { setStep(4); onClaimed(); return; }
        if (n?.status === "failed") throw new Error(n.error || "The payment didn't go through, so nothing was charged. Please try again.");
      }
      throw new Error("Minting is taking longer than usual. Check back in a few minutes.");
    } catch (e) { fail(e); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="bg-[#081820] border-amber-400/40" data-testid="claim-nft-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-amber-100">Claim #{look.no} as an NFT</DialogTitle>
          <DialogDescription className="text-slate-400">{look.name} is minted once, straight to your Solana wallet. You pay a small SOL cost and see the exact amount before paying. You keep wearing it in the game.</DialogDescription>
        </DialogHeader>
        <Steps step={step} />
        {step === 4 ? (
          <div className="text-center space-y-3" data-testid="claim-done">
            <div className="relative w-fit mx-auto"><MascotPortrait size="w-20 h-20" /><Check className="absolute -right-1 -bottom-1 w-7 h-7 p-1 rounded-full bg-emerald-400 text-black" /></div>
            <p className="text-[11px] uppercase tracking-[0.3em] font-mono-game text-amber-300">King Wolf says</p>
            <p className="font-display text-xl text-white" data-testid="claim-thanks">Thank you for joining the holders, pack legend!</p>
            <p className="text-sm text-slate-300">#{look.no} {look.name} is minted and in your wallet. Show it off:</p>
            <ShareButtons className="justify-center sm:justify-center" tid="claim-share" title={`#${look.no} ${look.name} · Wolfpack Games 1/1`}
              url={`${process.env.REACT_APP_BACKEND_URL}/api/share/look/${look.id}`}
              text={`I just minted #${look.no} ${look.name}, my one-of-a-kind 1/1 wolf from Wolfpack Games, on Solana! Come hunt with the pack.`} />
            <button onClick={onClose} data-testid="claim-close-btn" className="ember-btn px-5 py-2 rounded-full text-xs">CLOSE</button>
          </div>
        ) : busy ? (
          <p className="flex items-center justify-center gap-2 text-sm text-slate-300 py-4" data-testid="claim-busy"><Loader2 className="w-4 h-4 animate-spin" />
            {step === 0 ? "Waiting for your wallet…" : step === 1 ? "Approve the free message in your wallet…" : step === 2 ? (again ? "One more approval needed. Please approve the payment again in your wallet…" : "Approve the payment in your wallet…") : "Minting your NFT, usually under a minute…"}</p>
        ) : step === 2 && quote ? (
          <div className="space-y-3">
            <Cost cost={quote.cost} enough={quote.status === "ready"} />
            {quote.status === "ready"
              ? <button data-testid="claim-pay-btn" onClick={pay} className="w-full py-3 rounded-full bg-gradient-to-r from-amber-300 to-yellow-500 text-black text-sm font-bold inline-flex items-center justify-center gap-1.5 hover:brightness-110 transition-[filter]"><SolMark /> PAY & MINT</button>
              : <button data-testid="claim-retry-btn" onClick={() => { setStep(0); setQuote(null); }} className="w-full py-3 rounded-full border border-slate-600 text-sm text-white hover:border-amber-300">I'VE ADDED SOL, TRY AGAIN</button>}
          </div>
        ) : wallets.length ? (          <div className="grid gap-2">
            {wallets.map((w) => (
              <button key={w.id} data-testid={`claim-wallet-${w.id}`} onClick={() => go(w.id)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-700 hover:border-amber-300 text-left text-white transition-colors">
                <Wallet className="w-4 h-4 text-amber-300" /> Connect {w.name}
              </button>
            ))}
          </div>
        ) : (
          <div className="text-sm text-slate-300 space-y-3" data-testid="claim-no-wallet">
            <p>You need a Solana wallet such as Phantom, Solflare or Backpack.</p>
            <div className="flex flex-wrap gap-2">
              <a href="https://phantom.app/download" target="_blank" rel="noreferrer" data-testid="claim-get-phantom" className="ember-btn px-4 py-2 rounded-full text-xs inline-flex items-center gap-1">GET PHANTOM <ExternalLink className="w-3 h-3" /></a>
              <a href={openInPhantom()} data-testid="claim-open-phantom" className="px-4 py-2 rounded-full text-xs border border-slate-600 hover:border-amber-300">On a phone? Open in Phantom</a>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
